/**
 * vertical-video-card custom element
 *
 * Performance strategy:
 *  - Video <source> tags are NOT in the HTML. URLs are stored in data-sources
 *    and injected only on first interaction, so the browser makes zero video
 *    network requests on page load.
 *  - Desktop hover is debounced (250 ms) to avoid loading on accidental
 *    cursor passes.
 *  - IntersectionObserver pauses/stops any playing video when it leaves the
 *    viewport.
 *  - Only one video can play at a time — starting a new one stops the others.
 *
 * Desktop (mouse): hover 250 ms → plays muted silently.
 *                  Leave → pause + reset to poster.
 * Any device (play button): unmutes, shows native controls.
 * Stop button: resets entirely, restores play button.
 */

if (!customElements.get('vertical-video-card')) {
  class VerticalVideoCard extends HTMLElement {
    connectedCallback() {
      this._video   = this.querySelector('.vertical-videos__video');
      this._playBtn = this.querySelector('.vertical-videos__play-btn');
      this._stopBtn = this.querySelector('.vertical-videos__stop-btn');

      if (!this._video) return;

      this._sourcesLoaded = false;
      this._hoverTimer    = null;

      this._onEnter     = this._handleEnter.bind(this);
      this._onLeave     = this._handleLeave.bind(this);
      this._onPlayClick = this._handlePlayClick.bind(this);
      this._onStopClick = this._handleStopClick.bind(this);

      this.addEventListener('pointerenter', this._onEnter);
      this.addEventListener('pointerleave', this._onLeave);
      this._playBtn?.addEventListener('click', this._onPlayClick);
      this._stopBtn?.addEventListener('click', this._onStopClick);

      // Pause / stop when the card scrolls out of view
      this._observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            if (this.classList.contains('is-playing')) {
              this._handleStopClick();
            } else if (this.classList.contains('is-active')) {
              this._video.pause();
              this.classList.remove('is-active');
            }
          }
        },
        { threshold: 0.1 }
      );
      this._observer.observe(this);
    }

    disconnectedCallback() {
      clearTimeout(this._hoverTimer);
      this._observer?.disconnect();
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
      this._playBtn?.removeEventListener('click', this._onPlayClick);
      this._stopBtn?.removeEventListener('click', this._onStopClick);
    }

    /** Inject <source> tags the first time we actually need the video */
    _loadSources() {
      if (this._sourcesLoaded) return;
      try {
        const sources = JSON.parse(this._video.dataset.sources || '[]');
        sources.forEach(({ src, type }) => {
          const s = document.createElement('source');
          s.src  = src;
          s.type = type;
          this._video.appendChild(s);
        });
        this._video.load();
      } catch (e) {
        // Malformed JSON — silently skip
      }
      this._sourcesLoaded = true;
    }

    /** Stop all other cards that are currently playing */
    _stopOthers() {
      document.querySelectorAll('vertical-video-card.is-playing').forEach(card => {
        if (card !== this) card._handleStopClick();
      });
    }

    /** Desktop hover start — debounced 250 ms */
    _handleEnter(e) {
      if (e.pointerType !== 'mouse') return;
      if (this.classList.contains('is-playing')) return;

      this._hoverTimer = setTimeout(() => {
        this._loadSources();
        this._video.play().catch(() => {});
        this.classList.add('is-active');
      }, 250);
    }

    /** Desktop hover end */
    _handleLeave(e) {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(this._hoverTimer);
      if (this.classList.contains('is-playing')) return;

      this._video.pause();
      this._video.currentTime = 0;
      this.classList.remove('is-active');
    }

    /** Explicit play (any device) */
    _handlePlayClick() {
      this._stopOthers();
      this._loadSources();

      this._video.muted    = false;
      this._video.loop     = false;
      this._video.controls = true;

      this._video.play().catch(() => {
        // Unmuted autoplay blocked — fall back to muted
        this._video.muted = true;
        this._video.play().catch(() => {});
      });

      this.classList.add('is-active', 'is-playing');
    }

    /** Stop — reset to poster + play button */
    _handleStopClick() {
      clearTimeout(this._hoverTimer);
      this._video.pause();
      this._video.currentTime = 0;
      this._video.controls    = false;
      this._video.muted       = true;
      this._video.loop        = true;
      this.classList.remove('is-active', 'is-playing');
    }
  }

  customElements.define('vertical-video-card', VerticalVideoCard);
}
